import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Button,
 // Snackbar,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as ViewIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import DataTable, { Column } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { licensesApi, License, Subscription, Product } from '@/api/licenses-api';
import { usersApi, Customer } from '@/api/users-api';

export default function LicensesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    subscriptionId: '',
    customerId: '',
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Parse URL parameters for initial filter state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filter');
    
    if (filterParam === 'expiring') {
      setFilters(prev => ({ ...prev, status: 'expiring' }));
      setShowFilters(true);
    } else if (filterParam === 'expired') {
      setFilters(prev => ({ ...prev, status: 'expired' }));
      setShowFilters(true);
    }
  }, [location]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all licenses
      const licensesData = await licensesApi.getLicenses();
      setLicenses(licensesData);
      
      // Fetch subscriptions for filtering
      const subscriptionsData = await licensesApi.getSubscriptions();
      setSubscriptions(subscriptionsData);
      
      // Fetch products for display
      const productsData = await licensesApi.getProducts();
      setProducts(productsData);
      
      // Fetch customers for filtering (if not customer role)
      if (user?.role !== 'customer') {
        const customersData = await usersApi.getCustomers();
        setCustomers(customersData);
      }
    } catch (err) {
      console.error('Error fetching license data:', err);
      setError('Failed to load licenses. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user?.role]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters({ ...filters, [field]: value });
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      subscriptionId: '',
      customerId: '',
      search: '',
    });
  };

  // Apply filters to the license list
  const filteredLicenses = licenses.filter(license => {
    // Filter by status
    if (filters.status && filters.status !== 'expiring') {
      if (license.status !== filters.status) return false;
    } else if (filters.status === 'expiring') {
      // Check if license is expiring within 30 days
      const now = new Date();
      const expirationDate = new Date(license.expirationDate);
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(now.getDate() + 30);
      
      if (!(expirationDate <= thirtyDaysFromNow && expirationDate >= now && license.status === 'active')) return false;
    }
    
    // Filter by subscription
    if (filters.subscriptionId && license.subscriptionId !== filters.subscriptionId) return false;
    
    // Filter by customer (indirect through subscription)
    if (filters.customerId) {
      const subscription = subscriptions.find(sub => sub.subscriptionId === license.subscriptionId);
      if (!subscription || subscription.customerId !== filters.customerId) return false;
    }
    
    // Filter by search term (license key)
    if (filters.search && !license.licenseKey.toLowerCase().includes(filters.search.toLowerCase())) return false;
    
    return true;
  });

  const columns: Column<License>[] = [
    {
      id: 'licenseKey',
      label: 'License Key',
      minWidth: 200,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 120,
      format: (value) => <StatusChip status={value} type="license" />,
    }
  ];

  // Add customer column for distributor/reseller view
  if (user?.role !== 'customer') {
    columns.push({
      id: 'subscriptionId',
      label: 'Customer',
      minWidth: 150,
      format: (value) => {
        const subscription = subscriptions.find(sub => sub.subscriptionId === value);
        if (!subscription) return 'Unknown';
        
        const customer = customers.find(c => c.customerId === subscription.customerId);
        return customer ? customer.name : 'Unknown';
      },
    });
  }

  // Add product column
  columns.push({
    id: 'subscriptionId',
    label: 'Product',
    minWidth: 150,
    format: (value) => {
      const subscription = subscriptions.find(sub => sub.subscriptionId === value);
      if (!subscription) return 'Unknown';
      
      const product = products.find(p => p.productId === subscription.productId);
      return product ? product.description : 'Unknown';
    },
  });

  // Add additional columns
  columns.push(
    {
      id: 'issueDate',
      label: 'Issue Date',
      minWidth: 120,
      format: (value) => new Date(value).toLocaleDateString(),
    },
    {
      id: 'expirationDate',
      label: 'Expiration Date',
      minWidth: 120,
      format: (value, row) => {
        const expirationDate = new Date(value);
        const now = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(now.getDate() + 30);
        
        const isExpiringSoon = expirationDate <= thirtyDaysFromNow && expirationDate >= now && row.status === 'active';
        const isExpired = expirationDate < now || row.status === 'expired';
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {expirationDate.toLocaleDateString()}
            {isExpiringSoon && (
              <Chip 
                label="Expiring Soon" 
                color="warning" 
                size="small" 
                sx={{ ml: 1 }}
              />
            )}
            {isExpired && (
              <Chip 
                label="Expired" 
                color="error" 
                size="small" 
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        );
      },
    }
  );

  return (
    <>
      <PageHeader
        title="Licenses"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Licenses' },
        ]}
      />

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Filters
              <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ ml: 1 }}>
                <FilterIcon />
              </IconButton>
            </Typography>
            
            <Box>
              <Button 
                size="small" 
                startIcon={<ClearIcon />} 
                onClick={clearFilters}
                disabled={!Object.values(filters).some(Boolean)}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
          
          {showFilters && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3}}>
                <TextField
                  fullWidth
                  label="Search by License Key"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid size={{ xs: 12, md: 3}}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="issued">Issued</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="expiring">Expiring Soon</MenuItem>
                </TextField>
              </Grid>
              
              {user?.role !== 'customer' && (
                <Grid size={{ xs: 12, md: 3}}>
                  <TextField
                    select
                    fullWidth
                    label="Customer"
                    value={filters.customerId}
                    onChange={(e) => handleFilterChange('customerId', e.target.value)}
                  >
                    <MenuItem value="">All Customers</MenuItem>
                    {customers.map((customer) => (
                      <MenuItem key={customer.customerId} value={customer.customerId}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              
              <Grid size={{ xs: 12, md: 3}}>
                <TextField
                  select
                  fullWidth
                  label="Subscription"
                  value={filters.subscriptionId}
                  onChange={(e) => handleFilterChange('subscriptionId', e.target.value)}
                >
                  <MenuItem value="">All Subscriptions</MenuItem>
                  {subscriptions.map((subscription) => {
                    // Find the product for display
                    const product = products.find(p => p.productId === subscription.productId);
                    return (
                      <MenuItem key={subscription.subscriptionId} value={subscription.subscriptionId}>
                        {product?.description || 'Unknown'} - {subscription.chargebeeSubscriptionId.substring(0, 8)}...
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            </Grid>
          )}
          
          {/* Active filters display */}
          {Object.values(filters).some(Boolean) && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {filters.status && (
                <Chip 
                  label={`Status: ${filters.status === 'expiring' ? 'Expiring Soon' : filters.status}`}
                  onDelete={() => handleFilterChange('status', '')}
                  size="small"
                />
              )}
              
              {filters.customerId && (
                <Chip 
                  label={`Customer: ${customers.find(c => c.customerId === filters.customerId)?.name || 'Unknown'}`}
                  onDelete={() => handleFilterChange('customerId', '')}
                  size="small"
                />
              )}
              
              {filters.subscriptionId && (
                <Chip 
                label={`Subscription: ${subscriptions.find(s => s.subscriptionId === filters.subscriptionId)?.chargebeeSubscriptionId.substring(0, 8) || 'Unknown'}...`}
                onDelete={() => handleFilterChange('subscriptionId', '')}
                size="small"
              />
            )}
            
            {filters.search && (
              <Chip 
                label={`Search: ${filters.search}`}
                onDelete={() => handleFilterChange('search', '')}
                size="small"
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>

    {error && (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    )}

    <DataTable
      columns={columns}
      data={filteredLicenses}
      loading={loading}
      getRowId={(row) => row.licenseId}
      onRowClick={(row) => navigate(`/licenses/${row.licenseId}`)}
      actions={[
        {
          icon: <ViewIcon />,
          label: 'View Details',
          onClick: (row) => navigate(`/licenses/${row.licenseId}`),
        },
      ]}
      emptyMessage={
        Object.values(filters).some(Boolean)
          ? "No licenses match the current filters."
          : "No licenses found."
      }
    />
  </>
);
}